import React, { useState, useEffect } from 'react';
import AdminStats from './AdminStats';
import AdminPoolTable  from './AdminPoolTable';
import AdminMockData  from './AdminMockData';
import AdminBetLog from './AdminBetLog';
import AdminControls from './AdminControls';
import AdminUsers from './AdminUsers';
import AdminLogs from './AdminLogs';
import AdminTradeLog from './AdminTradeLog';
import AdminArchive from './AdminArchive';
import AdminUsdcChart from './AdminUsdcChart';
import AdminEmail from './AdminEmail';

/**
 * AdminDashboard — Full house control center.
 *
 * Tabs:
 *   - Overview: Revenue, pools, settlements (existing)
 *   - Controls: Emergency shutdown, wallet status
 *   - Users: Account management, ban/unban, balance corrections
 *   - Logs: Full activity audit trail
 *
 * Server stats auto-refresh every 15 seconds.
 */
const AdminDashboard = ({ houseFee, pools, bets, ledger, prices }) => {
    const [tab, setTab] = useState('overview');
    const [serverStats, setServerStats] = useState(null);

    useEffect(() => {
        fetchServerStats();
        const interval = setInterval(fetchServerStats, 15000);
        return () => clearInterval(interval);
    }, []);

    const fetchServerStats = async () => {
        try {
            const res = await fetch('/api/admin/stats', { credentials: 'include' });
            if (res.ok) setServerStats(await res.json());
        } catch { /* ignore */ }
    };

    const tabs = [
        { id: 'overview', label: 'Overview',  icon: '📊' },
        { id: 'trades',   label: 'Trade Log', icon: '📒' },
        { id: 'archive',  label: 'Archive',   icon: '🗄️' },
        { id: 'controls', label: 'Controls',  icon: '⚡' },
        { id: 'users',    label: 'Users',     icon: '👥' },
        { id: 'email',    label: 'Email',     icon: '✉️' },
        { id: 'logs',     label: 'Logs',      icon: '📋' },
        { id: 'mock',     label: 'Mock Data', icon: '🤖' },
    ];

    return (
        <div className="px-6 py-4 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4 mb-4">
                <h1 className="text-2xl font-black tracking-tight text-white">
                    House Dashboard
                </h1>
                <span className="text-sm px-3 py-1.5 rounded bg-amber-500/10 text-amber-400 font-black uppercase tracking-[0.2em] border border-amber-500/20">
                    Admin
                </span>
                <div className="flex-1" />
                {/* Server stats badges */}
                {serverStats && (
                    <div className="flex items-center gap-3">
                        <StatBadge label="Users" value={serverStats.users} color="blue" />
                        <StatBadge label="Bets" value={serverStats.bets?.total} color="purple" />
                        <StatBadge label="Volume" value={`${((serverStats.bets?.volumeSol || 0) * 100).toFixed(0)} cr`} color="emerald" />
                        <HouseBalanceLedger
                            credits={serverStats.revenue?.houseBalanceCredits || 0}
                            balanceSol={serverStats.revenue?.houseBalanceSol || 0}
                            prices={prices}
                        />
                    </div>
                )}
                <div className="flex items-center gap-2 ml-4">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-sm text-emerald-500/80 font-bold uppercase tracking-[0.2em]">
                        Live
                    </span>
                </div>
            </div>

            {/* Price bar */}
            <div className="flex gap-4 mb-4 overflow-x-auto pb-1">
                {Object.entries(prices).map(([coinId, data]) => (
                    <div key={coinId} className="flex items-center gap-2 px-3 py-1.5 border border-white/5 bg-white/[0.02] rounded-md shrink-0">
                        <span className="text-xs uppercase tracking-wider text-gray-500 font-bold">
                            {coinId.slice(0, 4).toUpperCase()}
                        </span>
                        <span className="font-mono text-sm text-white font-bold">
                            ${data?.usd?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </span>
                    </div>
                ))}
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-white/10 mb-6">
                {tabs.map(t => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        className={`px-5 py-3 text-xs uppercase tracking-[0.2em] font-black transition-all ${tab === t.id
                            ? 'text-white border-b-2 border-blue-500'
                            : 'text-gray-500 hover:text-gray-300'
                            }`}
                    >
                        <span className="mr-1.5">{t.icon}</span>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {tab === 'overview' && (
                <>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                        <div className="lg:col-span-2">
                            <AdminStats houseFee={houseFee} pools={pools} bets={bets} ledger={ledger} />
                        </div>
                        <div>
                            <AdminUsdcChart />
                        </div>
                    </div>
                    <AdminPoolTable pools={pools} />
                    <AdminBetLog bets={bets} ledger={ledger} />
                </>
            )}

            {tab === 'controls' && <AdminControls />}
            {tab === 'trades'   && <AdminTradeLog />}
            {tab === 'archive'  && <AdminArchive />}
            {tab === 'users'    && <AdminUsers />}
            {tab === 'email'    && <AdminEmail onClose={() => setTab('overview')} />}
            {tab === 'logs' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                        <AdminLogs />
                    </div>
                    <div>
                        <AdminUsdcChart />
                    </div>
                </div>
            )}
            {tab === 'mock' && <AdminMockData />}
        </div>
    );
};

/** Compact stat badge for the header. */
const StatBadge = ({ label, value, color, onClick }) => (
    <div
        className={`flex items-center gap-2 px-3.5 py-1.5 rounded border border-${color}-500/20 bg-${color}-500/5 ${onClick ? 'cursor-pointer hover:bg-' + color + '-500/15 transition-colors' : ''}`}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
    >
        <span className={`text-sm uppercase tracking-wider text-${color}-500/70 font-bold`}>
            {label}
        </span>
        <span className={`text-base font-mono font-bold text-${color}-400 ${onClick ? 'underline decoration-dotted underline-offset-2' : ''}`}>
            {value ?? '—'}
        </span>
    </div>
);

/**
 * HouseBalanceLedger — Two-box admin-only revenue display.
 *
 * Box 1: House credit balance (sum of all fees collected, in credits)
 * Box 2: Live SOL cash equivalent (credits ÷ 100 × SOL/USD price)
 *
 * The SOL price streams in real-time from the prices prop.
 */
const HouseBalanceLedger = ({ credits, balanceSol, prices }) => {
    // Use SOL price from live prices if available
    const solPriceUsd = prices?.solana?.usd || prices?.['solana']?.usd || null;
    const cashUsd = solPriceUsd ? (balanceSol * solPriceUsd) : null;

    return (
        <div className="flex items-stretch gap-[1px]">
            {/* Box 1 — House Credits Balance */}
            <div className="flex flex-col justify-center px-3.5 py-1.5 rounded-l border border-amber-500/30 bg-amber-500/5 min-w-[130px]">
                <span className="text-[9px] uppercase tracking-[0.18em] text-amber-500/60 font-black leading-none mb-1">
                    HOUSE BALANCE
                </span>
                <span className="font-mono text-base font-black text-amber-400 leading-none">
                    {credits.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    <span className="text-[9px] text-amber-500/50 ml-1 font-bold">credits</span>
                </span>
            </div>

            {/* Box 2 — Live SOL Cash Equivalent */}
            <div className="flex flex-col justify-center px-3.5 py-1.5 rounded-r border border-emerald-500/20 bg-emerald-500/5 min-w-[130px]">
                <span className="text-[9px] uppercase tracking-[0.18em] text-emerald-500/60 font-black leading-none mb-1">
                    CASH VALUE
                </span>
                <span className="font-mono text-base font-black text-emerald-400 leading-none">
                    {cashUsd !== null
                        ? `$${cashUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : `${balanceSol.toFixed(4)} SOL`
                    }
                    {solPriceUsd && (
                        <span className="text-[9px] text-emerald-500/40 ml-1 font-bold">
                            @ ${solPriceUsd.toLocaleString()}
                        </span>
                    )}
                </span>
            </div>
        </div>
    );
};

export default AdminDashboard;
